import React from 'react'
import { components } from 'react-select'
import { useDebounce } from 'hooks/useDebounce'
import { useFormContext } from 'react-hook-form'

import { formatString } from 'utils/formatters'
import { useStores } from 'stores'
import { validateMaxLength, validateMinimum, validateSpecialCharacters } from 'utils/validators' 
import FileAttachment from 'common/Forms/FileAttachment'
import Input from 'common/Forms/Input'
import PlaceholderOption from 'common/PlaceholderOption'
import ReactSelect from 'common/Forms/ReactSelect'
import ShowMoreButton from 'common/ShowMoreButton'
import WebformProductLineInfoQty from 'components/Webform/WebformProductLineInfoQty'

export default ({ authStore, resource, lineItem, index, data }) => {
  const { setValue } = useFormContext()
  const [category, setCategory] = React.useState(lineItem.category)
  const [subCategory, setSubCategory] = React.useState(lineItem.subCategory)
  const [isDisableSubCategory, setIsDisableSubCategory] = React.useState(false) 
  const [filteredBaseCategories, setFilteredBaseCategories] = React.useState(data.categories)
  const [filteredBaseSubCategories, setFilteredBaseSubCategories] = React.useState(data.subcategories || [])
  
  // Category pagination state
  const [categoryInputValue, setCategoryInputValue] = React.useState('')
  const [categoryOptions, setCategoryOptions] = React.useState([])
  const [categoryPage, setCategoryPage] = React.useState(1)
  const [categoryLoadingMore, setCategoryLoadingMore] = React.useState(false)
  const [categoryMenuIsOpen, setCategoryMenuIsOpen] = React.useState(undefined)
  const [categoryHasSelected, setCategoryHasSelected] = React.useState(false)
  const categoryPageSize = 50

  // SubCategory pagination state
  const [subCategoryInputValue, setSubCategoryInputValue] = React.useState('')
  const [subCategoryOptions, setSubCategoryOptions] = React.useState([])
  const [subCategoryPage, setSubCategoryPage] = React.useState(1)
  const [subCategoryLoadingMore, setSubCategoryLoadingMore] = React.useState(false)
  const [subCategoryMenuIsOpen, setSubCategoryMenuIsOpen] = React.useState(undefined)
  const [subCategoryHasSelected, setSubCategoryHasSelected] = React.useState(false)
  const subCategoryPageSize = 50

  const { pageResourceStore } = useStores()
  

  const {
    AppSession: { session },
  } = authStore
  const { settings = {}, glAccounts = [] } = data
  const {
    PageResource: { common: commonResource = {} },
  } = pageResourceStore

  // Load category options with pagination
  const loadCategoryOptions = React.useCallback(
    async (search, page) => {
      setCategoryLoadingMore(true)
      
      // Wrap in setTimeout to make it truly async and prevent blocking
      await new Promise(resolve => setTimeout(resolve, 0))
      
      // Use filtered base categories (which may be filtered by subcategory)
      let filteredCategories = [...filteredBaseCategories]
      if (search && search.trim()) {
        filteredCategories = filteredCategories.filter(cat => 
          cat.name.toLowerCase().includes(search.toLowerCase())
        )
      }
      
      // Simulate pagination
      const startIndex = (page - 1) * categoryPageSize
      const endIndex = startIndex + categoryPageSize
      const paginatedData = filteredCategories.slice(startIndex, endIndex)
      
      setCategoryLoadingMore(false)

      if (page === 1) {
        setCategoryOptions(paginatedData)
      } else {
        setCategoryOptions(prev => [...prev, ...paginatedData])
      }

      // Only focus if there's a search term or menu is open (user is actively interacting)
      if ((search && search.trim()) || categoryMenuIsOpen) {
        const input = document.querySelector(`[id="webformLines.${index}.category"] input`)
        if (input) {
          input.focus()
        }
      }
    },
    [filteredBaseCategories, categoryPageSize, index, categoryMenuIsOpen]
  )

  // Load subcategory options with pagination
  const loadSubCategoryOptions = React.useCallback(
    async (search, page) => {
      setSubCategoryLoadingMore(true)
      
      // Wrap in setTimeout to make it truly async and prevent blocking
      await new Promise(resolve => setTimeout(resolve, 0))
      
      // Use filtered base subcategories (which may be filtered by category)
      let filteredSubCategories = [...filteredBaseSubCategories]
      if (search && search.trim()) {
        filteredSubCategories = filteredSubCategories.filter(subCat => 
          subCat.name.toLowerCase().includes(search.toLowerCase())
        )
      }
      
      // Simulate pagination
      const startIndex = (page - 1) * subCategoryPageSize
      const endIndex = startIndex + subCategoryPageSize
      const paginatedData = filteredSubCategories.slice(startIndex, endIndex)
      
      setSubCategoryLoadingMore(false)

      if (page === 1) {
        setSubCategoryOptions(paginatedData)
      } else {
        setSubCategoryOptions(prev => [...prev, ...paginatedData])
      }

      // Only focus if there's a search term or menu is open (user is actively interacting)
      if ((search && search.trim()) || subCategoryMenuIsOpen) {
        const input = document.querySelector(`[id="webformLines.${index}.subCategory"] input`)
        if (input) {
          input.focus()
        }
      }
    },
    [filteredBaseSubCategories, subCategoryPageSize, index, subCategoryMenuIsOpen]
  )

  React.useEffect(() => {
    if (category) {
      handleCategoryChange(category)
    }
  }, [])

  React.useEffect(() => {
    loadCategoryOptions(categoryInputValue, categoryPage)
  }, [categoryInputValue, categoryPage, loadCategoryOptions])

  React.useEffect(() => {
    loadSubCategoryOptions(subCategoryInputValue, subCategoryPage)
  }, [subCategoryInputValue, subCategoryPage, loadSubCategoryOptions])

  const debouncedSetCategoryInputValue = useDebounce(value => {
    setCategoryInputValue(value)
    setCategoryPage(1)
    setCategoryHasSelected(false)
  }, 300)

  const debouncedSetSubCategoryInputValue = useDebounce(value => {
    setSubCategoryInputValue(value)
    setSubCategoryPage(1)
    setSubCategoryHasSelected(false)
  }, 300)

  const handleCategoryInputChange = (newValue, actionMeta) => {
    // Only handle user input, not other events
    if (actionMeta.action === 'input-change') {
      debouncedSetCategoryInputValue(newValue)
      return newValue
    }
    return newValue
  }

  const handleSubCategoryInputChange = (newValue, actionMeta) => {
    // Only handle user input, not other events
    if (actionMeta.action === 'input-change') {
      debouncedSetSubCategoryInputValue(newValue)
      return newValue
    }
    return newValue
  }

  const handleCategoryChange = selected => {
    // Clear the input value after selecting an option
    setCategoryInputValue('')
    setCategoryPage(1)
    setCategoryHasSelected(true)

    if (selected) {
      const newSubCategories = data.subcategories.filter(item => item.categoryNames.includes(selected.name))
      if (newSubCategories && newSubCategories.length) {
        setIsDisableSubCategory(false)
        setFilteredBaseSubCategories(newSubCategories)
      } else {
        setIsDisableSubCategory(true)
        setSubCategory(null)
        setValue(`webformLines.${index}.subCategory`, null)
        setFilteredBaseSubCategories([])
      }
      // Reset subcategory pagination
      setSubCategoryPage(1)
      setSubCategoryInputValue('')
    } else {
      setFilteredBaseSubCategories([...data.subcategories])
      setIsDisableSubCategory(false)
      setSubCategory(null)
      setValue(`webformLines.${index}.subCategory`, null)
      // Reset subcategory pagination
      setSubCategoryPage(1)
      setSubCategoryInputValue('')
    }
    setCategory(selected)
  }

  const handleCategoryMenuOpen = () => {
    setCategoryMenuIsOpen(true)
  }

  const handleCategoryMenuClose = () => {
    setCategoryMenuIsOpen(false)
  }

  const handleCategoryBlur = () => {
    // When input loses focus and no selection was made, load default list
    const hasSearchTerm = categoryInputValue && categoryInputValue.trim().length > 0

    if (hasSearchTerm && !categoryHasSelected) {
      // Load default list without search term
      setCategoryInputValue('')
      setCategoryPage(1)
      setCategoryHasSelected(false)
    }
  }

  const handleSubCategoryChange = selected => {
    // Clear the input value after selecting an option
    setSubCategoryInputValue('')
    setSubCategoryPage(1)
    setSubCategoryHasSelected(true)

    if (selected) {
      // Filter categories based on selected subcategory
      const newCategories = data.categories.filter(
        item => item.subcategories && item.subcategories.includes(selected.name)
      )
      setFilteredBaseCategories(newCategories)
      // Reset category pagination to show filtered categories
      setCategoryPage(1)
      setCategoryInputValue('')
    } else {
      // Reset to all categories
      setFilteredBaseCategories([...data.categories])
      // Reset category pagination
      setCategoryPage(1)
      setCategoryInputValue('')
    }
    setSubCategory(selected)
  }

  const handleSubCategoryMenuOpen = () => {
    setSubCategoryMenuIsOpen(true)
  }

  const handleSubCategoryMenuClose = () => {
    setSubCategoryMenuIsOpen(false)
  }

  const handleSubCategoryBlur = () => {
    // When input loses focus and no selection was made, load default list
    const hasSearchTerm = subCategoryInputValue && subCategoryInputValue.trim().length > 0

    if (hasSearchTerm && !subCategoryHasSelected) {
      // Load default list without search term
      setSubCategoryInputValue('')
      setSubCategoryPage(1)
      setSubCategoryHasSelected(false)
    }
  }

  const categoryOptionsWithLoadMore = React.useMemo(() => {
    const result = Array.isArray(categoryOptions) ? [...categoryOptions] : []
    const totalLoaded = result.length
    
    // Check if there are more items to load based on filtered base categories
    let totalAvailable = filteredBaseCategories.length
    if (categoryInputValue && categoryInputValue.trim()) {
      totalAvailable = filteredBaseCategories.filter(cat => 
        cat.name.toLowerCase().includes(categoryInputValue.toLowerCase())
      ).length
    }
    
    const hasMore = totalLoaded > 0 && totalLoaded < totalAvailable && totalLoaded === categoryPageSize * categoryPage

    if (hasMore) {
      result.push({ isLoadMore: true, label: resource.ShowMore })
    }

    return result
  }, [categoryOptions, categoryPage, categoryPageSize, resource, filteredBaseCategories, categoryInputValue])

  const subCategoryOptionsWithLoadMore = React.useMemo(() => {
    const result = Array.isArray(subCategoryOptions) ? [...subCategoryOptions] : []
    const totalLoaded = result.length
    
    // Check if there are more items to load based on filtered base subcategories
    let totalAvailable = filteredBaseSubCategories.length
    if (subCategoryInputValue && subCategoryInputValue.trim()) {
      totalAvailable = filteredBaseSubCategories.filter(subCat => 
        subCat.name.toLowerCase().includes(subCategoryInputValue.toLowerCase())
      ).length
    }
    
    const hasMore = totalLoaded > 0 && totalLoaded < totalAvailable && totalLoaded === subCategoryPageSize * subCategoryPage

    if (hasMore) {
      result.push({ isLoadMore: true, label: resource.ShowMore })
    }

    return result
  }, [subCategoryOptions, subCategoryPage, subCategoryPageSize, resource, filteredBaseSubCategories, subCategoryInputValue])
 
  const isTotalBudgetRequired = !!session.clientController?.wf || settings.isWfBudgetThresholdEnabled
  const isShowExtendedDescriptionPlaceholder = session.clientAbbreviation.startsWith('ELIA')
  const maxDescriptionLength = settings.erp === 'SAP' ? 40 : 150
  const clientCurrency = data.clientCurrency

  return (
    <React.Fragment>
      <ReactSelect
        id={`webformLines.${index}.category`}
        name={`webformLines.${index}.category`}
        rules={{ required: resource.Required }}
        label={`${resource.Category} *`}
        placeholder={resource.SelectAppropriateCategory}
        options={categoryOptionsWithLoadMore}
        optionValueKey="id"
        optionLabelKey="name"
        formGroup
        horizontal
        isClearable
        isSearchable={true}
        onInputChange={handleCategoryInputChange}
        onChange={handleCategoryChange}
        onMenuOpen={handleCategoryMenuOpen}
        onMenuClose={handleCategoryMenuClose}
        onBlur={handleCategoryBlur}
        menuIsOpen={categoryMenuIsOpen}
        isLoading={categoryLoadingMore}
        filterOption={null}
        components={{
          Option: ({ data, innerRef, ...innerProps }) => {
            if (data.isPlaceholder) {
              return <PlaceholderOption ref={innerRef} label={data.label} />
            }

            if (data.isLoadMore) {
              return (
                <ShowMoreButton
                  resource={resource}
                  disabled={categoryLoadingMore}
                  innerProps={innerProps}
                  setPage={setCategoryPage}
                />
              )
            }

            return (
              <components.Option data={data} {...innerProps}>
                {data.name}
              </components.Option>
            )
          },
        }}
      />
      {settings.shouldSelectSubcategory && (
        <ReactSelect
          id={`webformLines.${index}.subCategory`}
          name={`webformLines.${index}.subCategory`}
          rules={{ required: !isDisableSubCategory && !!category && resource.Required }}
          value={!isDisableSubCategory ? subCategory : null}
          label={resource.SubCategory}
          placeholder={resource.SelectAppropriateSubcategory}
          options={subCategoryOptionsWithLoadMore}
          optionValueKey="id"
          optionLabelKey="name"
          formGroup
          horizontal
          isClearable
          isSearchable={true}
          isDisabled={isDisableSubCategory}
          onInputChange={handleSubCategoryInputChange}
          onChange={handleSubCategoryChange}
          onMenuOpen={handleSubCategoryMenuOpen}
          onMenuClose={handleSubCategoryMenuClose}
          onBlur={handleSubCategoryBlur}
          menuIsOpen={subCategoryMenuIsOpen}
          isLoading={subCategoryLoadingMore}
          filterOption={null}
          components={{
            Option: ({ data, innerRef, ...innerProps }) => {
              if (data.isPlaceholder) {
                return <PlaceholderOption ref={innerRef} label={data.label} />
              }

              if (data.isLoadMore) {
                return (
                  <ShowMoreButton
                    resource={resource}
                    disabled={subCategoryLoadingMore}
                    innerProps={innerProps}
                    setPage={setSubCategoryPage}
                  />
                )
              }

              return (
                <components.Option data={data} {...innerProps}>
                  {data.name}
                </components.Option>
              )
            },
          }}
        />
      )}
      {settings.enableGLAccount && (
        <ReactSelect
          name={`webformLines.${index}.glAccount`}
          label={`${resource.GLAccount} *`}
          placeholder={resource.SelectAppropriateGLAccount}
          options={glAccounts}
          optionValueKey="id"
          optionLabelKey="glAccount"
          formGroup
          horizontal
        />
      )}
      <Input
        name={`webformLines.${index}.articleDescription`}
        rules={{
          required: resource.Required,
          validate: {
            specialCharacters: value => {
              return (
                settings.allowSpecialCharacters || validateSpecialCharacters(value, commonResource.SpecialCharacter)
              )
            },
            maxLength: value => {
              return validateMaxLength(
                value,
                maxDescriptionLength,
                formatString(commonResource.MaxLength, maxDescriptionLength)
              )
            },
          },
        }}
        label={`${resource.ProductDescription} *`}
        placeholder={resource.phProductDescription}
        formGroup
        horizontal
      />
      <WebformProductLineInfoQty {...{ commonResource, resource, index }} />
      <Input
        name={`webformLines.${index}.budget.upperLimit`}
        rules={{
          required: isTotalBudgetRequired && commonResource.Number,
          validate: value => {
            return validateMinimum(value, 0, formatString(commonResource.MinValue, 0))
          },
        }}
        min="0"
        type="number"
        label={`${resource.TotalBudget} ${isTotalBudgetRequired ? '*' : ''}`}
        placeholder={resource.phTotalBudget}
        formGroup
        horizontal
        inputCol={{ md: 5 }}
        startAdornment={<span className="md-20 text-muted">{clientCurrency.currencySymbol}</span>}
      />
      <Input
        name={`webformLines.${index}.supplierPreference`}
        label={resource.PreferredSupplier}
        placeholder={resource.phPreferredSupplier}
        formGroup
        horizontal
      /> 
      <Input
        name={`webformLines.${index}.remarks`}
        type="textarea"
        label={resource.ExtendedDescription}
        {...(isShowExtendedDescriptionPlaceholder
          ? {
              placeholder: resource.phExtendedDescription,
            }
          : {})}
        formGroup
        horizontal
        rows="4"
      />
      <FileAttachment
        name={`webformLines.${index}.attachments`}
        label={resource.Attachments}
        formGroup
        horizontal
        formClassName="mb-4"
        hideNoAttachments
      />
    </React.Fragment>
  )
}